import type { ReactNode } from "react";
import { Link } from "react-router";

// Shared dashboard primitives used by Home's overview tiles.

// One "+ …" quick-action line, shared by the hover-reveal overlays.
export const QUICK_ACTION_CLASS =
  "-mx-1 flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-left text-[12px] text-ink/70 transition hover:bg-ink/[0.06] hover:text-accent";

export function QuickActionLink({
  to,
  children,
  icon = "+",
  className = "",
}: {
  to: string;
  children: ReactNode;
  icon?: ReactNode;
  // Layout-only additions (e.g. mt-auto to pin the link to the bottom of a taller container) —
  // appended after QUICK_ACTION_CLASS, not a replacement for it.
  className?: string;
}) {
  return (
    <Link to={to} className={`${QUICK_ACTION_CLASS} ${className}`}>
      <span className="text-ink/40">{icon}</span>
      {children}
    </Link>
  );
}

// Hover reveal lifted verbatim from PowerTile (components/design/PowerTile.tsx): artwork sits
// behind the tile content, hidden + blurred until hover. Needs a `group` + `relative
// overflow-hidden` ancestor, and to be the FIRST child so later siblings paint on top of it
// without an explicit z-index.
export function HeroRevealImage({ src }: { src: string }) {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]" aria-hidden>
      <img
        src={src}
        alt=""
        decoding="async"
        className="absolute inset-0 h-full w-full scale-110 object-cover opacity-0 blur-md transition-[transform,opacity,filter] duration-500 ease-out group-hover:scale-100 group-hover:opacity-100 group-hover:blur-none"
      />
      {/* Flat wash, not WEIGHT_TEXT_GRADIENT: that gradient is dense top/bottom and nearly clear
          in the middle, calibrated for PowerTile where the text sits in a bottom band only. Here
          the big value sits in the tile's vertical middle, exactly where the gradient is weakest —
          tried it, read worse than a flat wash (spark home page feedback). /80, not /55: the
          densest step of PowerTile's own WEIGHT_IMAGE_SCRIM ladder ("bg-page/80" ·
          "bg-page/60" · "bg-page/40" · "bg-page/15") — legibility must hold over any artwork, so
          reuse the strongest calibrated stop instead of inventing a new one. */}
      <div className="absolute inset-0 bg-page/80 opacity-0 transition-opacity duration-500 ease-out group-hover:opacity-100" />
    </div>
  );
}

// Compact dashboard tile: a KPI (number, optionally with a progress bar) that
// doubles as a quick jump. When `actions` are passed they stay collapsed and
// expand on hover / keyboard focus. The progress-bar row is always reserved so
// a tile WITH a bar isn't taller than one without, keeps tiles uniform.
export function StatTile({
  to,
  label,
  value,
  sub,
  bar,
  loading,
  actions,
  heroImageSrc,
}: {
  to: string;
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  bar?: number;
  loading?: boolean;
  actions?: ReactNode;
  // Section artwork (e.g. /heroes/save.jpg), revealed on hover. Demo-only polish (spark home page);
  // omit for tiles that shouldn't carry it.
  heroImageSrc?: string;
}) {
  return (
    // min-h-36, not -32: the row uses items-start (a hover-expanded tile must not stretch its
    // siblings — see Row/the grid comments in HomePage.tsx), so this floor is what keeps every
    // tile in a row the same collapsed height, not just a lower bound. StatTile's own content
    // (text-5xl value + the always-reserved bar row below) already clears -32 on its own; -36 is
    // sized to also cover SplitTileShell's shorter content (a text-3xl value, no bar row) so both
    // land on the same floor instead of the split tiles reading visibly shorter (spark home page
    // feedback).
    <div className="box group relative flex min-h-36 flex-col overflow-hidden bg-surface p-4 transition-[transform,box-shadow] hover:-translate-y-0.5 hover:ring-ink/15">
      {heroImageSrc ? <HeroRevealImage src={heroImageSrc} /> : null}
      {/* relative, NOT absolute: the hero image is absolutely positioned, and an absolutely
          positioned sibling always paints above a static one regardless of DOM order (CSS stacking
          rules) — without this wrapper the image sat on top of the text. `relative` moves this
          block into the same "positioned" paint group, where DOM order (after the image) wins,
          while staying in normal flow so the tile still grows with its content. */}
      <div className="relative flex flex-1 flex-col">
        <Link to={to} className="flex flex-col">
          {/* field-label, not an ad-hoc text-[10px]: UI_CONVENTIONS' field type scale — the
              canonical eyebrow-above-a-value size (11px), not a size outside that scale. */}
          <div className="field-label">{label}</div>
          {/* The tile's hero value uses the established text-5xl display size. */}
          <div className="mt-2 font-serif text-5xl leading-none tabular-nums">
            {loading ? <span className="text-ink/30">…</span> : value}
          </div>
          {/* Always reserve the progress-bar row so a tile WITH a bar isn't taller
              than one without, keeps every tile a uniform height. Invisible when
              no bar is passed. */}
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full">
            {typeof bar === "number" ? (
              <div className="h-full w-full rounded-full bg-ink/10">
                <div className="h-full rounded-full bg-accent" style={{ width: `${bar}%` }} />
              </div>
            ) : null}
          </div>
        </Link>
        {/* sub sits OUTSIDE the tile link so it can carry its own inline links
            (nested <a> would be invalid). */}
        {sub ? <div className="mt-1.5 text-[11px] leading-snug text-ink/55">{sub}</div> : null}
        {actions ? (
          // Actions expand the tile inline on hover / keyboard focus: grid-rows
          // 0fr→1fr animates to the exact content height, so the tile grows to fit.
          <div className="grid grid-rows-[0fr] opacity-0 transition-[grid-template-rows,opacity,margin-top] duration-500 ease-out group-hover:mt-3 group-hover:grid-rows-[1fr] group-hover:opacity-100 group-focus-within:mt-3 group-focus-within:grid-rows-[1fr] group-focus-within:opacity-100">
            <div className="flex min-h-0 flex-col gap-0.5 overflow-hidden">{actions}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// Shared frame for a tile split into two independently-linked halves: the box, hero-image reveal,
// the two-column grid and its centred divider (an explicit line rather than divide-x, so
// content-length differences between the halves — a one-line vs. three-line sub — can't read as
// if it drifted off-centre). SplitStatTile (below) fills it with the KPI shape (value + sub +
// hover-link); other callers needing two independently-linked columns of arbitrary content (e.g.
// two power lists side by side) pass their own.
export function SplitTileShell({
  left,
  right,
  heroImageSrc,
}: {
  left: ReactNode;
  right: ReactNode;
  heroImageSrc?: string;
}) {
  return (
    // min-h-36, not -32: the row uses items-start (a hover-expanded tile must not stretch its
    // siblings — see Row/the grid comments in HomePage.tsx), so this floor is what keeps every
    // tile in a row the same collapsed height, not just a lower bound. StatTile's own content
    // (text-5xl value + the always-reserved bar row below) already clears -32 on its own; -36 is
    // sized to also cover SplitTileShell's shorter content (a text-3xl value, no bar row) so both
    // land on the same floor instead of the split tiles reading visibly shorter (spark home page
    // feedback).
    <div className="box group relative flex min-h-36 flex-col overflow-hidden bg-surface p-4 transition-[transform,box-shadow] hover:-translate-y-0.5 hover:ring-ink/15">
      {heroImageSrc ? <HeroRevealImage src={heroImageSrc} /> : null}
      {/* Side by side, not stacked: stacking two halves would make this tile roughly twice the
          height of its StatTile neighbours in the same grid row. */}
      <div className="relative grid flex-1 grid-cols-2">
        <div
          className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-ink/10"
          aria-hidden="true"
        />
        <div className="flex flex-col pr-4">{left}</div>
        <div className="flex flex-col pl-4">{right}</div>
      </div>
    </div>
  );
}

// Two stats sharing one tile, each half its own link — for merging two low-traffic KPIs (e.g.
// Community + Pins) back to a single grid slot without losing either number. Not a variant of
// StatTile (single value, single link): different shape, so its own component per this folder's
// "one component per file, small internal helpers stay local" rule doesn't apply here since this
// IS the shared dashboard-primitives file.
export function SplitStatTile({
  left,
  right,
  heroImageSrc,
}: {
  left: { to: string; label: string; value: ReactNode; sub?: ReactNode; linkLabel: string };
  right: { to: string; label: string; value: ReactNode; sub?: ReactNode; linkLabel: string };
  heroImageSrc?: string;
}) {
  const halfContent = (h: typeof left) => (
    <>
      <div className="field-label">{h.label}</div>
      {/* One step below StatTile's text-5xl, same ratio as before (text-xl was one step below
          StatTile's old text-3xl): a half-width column has roughly half the horizontal room a
          full tile does. */}
      <div className="mt-1 font-serif text-3xl leading-none tabular-nums">{h.value}</div>
      {h.sub ? <div className="mt-1 text-[11px] leading-snug text-ink/55">{h.sub}</div> : null}
      {/* Same expand-on-hover mechanic as StatTile's own `actions` (grid-rows 0fr→1fr), but
          mt-auto instead of a fixed mt-3: the two links sit bottom-aligned with each other (spark
          home page feedback), not each pinned a fixed distance below its own differently-long sub
          text. The explicit link, not the whole half, stays a QuickActionLink rather than nesting
          it in a wrapping Link (invalid nested <a>). The outer (unnamed) group — same as
          StatTile — means hovering ANYWHERE on the tile reveals both links together. */}
      <div className="mt-auto grid grid-rows-[0fr] opacity-0 transition-[grid-template-rows,opacity] duration-500 ease-out group-hover:grid-rows-[1fr] group-hover:opacity-100 group-focus-within:grid-rows-[1fr] group-focus-within:opacity-100">
        <div className="min-h-0 overflow-hidden">
          <QuickActionLink to={h.to} icon="→">
            {h.linkLabel}
          </QuickActionLink>
        </div>
      </div>
    </>
  );
  return (
    <SplitTileShell
      heroImageSrc={heroImageSrc}
      left={halfContent(left)}
      right={halfContent(right)}
    />
  );
}
